/**
 * Lo PURO de las preferencias de pantalla (tanda 6): leer y guardar en `localStorage` con
 * saneo, sin tocar el navegador a menos que se le pase el almacén. El hook `usePreferencia`
 * es la parte con estado; aquí vive lo que se puede probar.
 */

/** Lo único que se le pide al almacén: así una prueba pasa un objeto cualquiera. */
export interface Almacen {
  getItem(clave: string): string | null;
  setItem(clave: string, valor: string): void;
}

export const PREFIJO_LOCAL = 'conversia.pref.';

/**
 * Dónde vive la copia local de una preferencia: `conversia.pref.<usuario>.<clave>`. Va por
 * usuario: dos cuentas en el mismo navegador no se pisan las vistas. Sin usuario cargado
 * todavía no hay dónde (`null`): no se lee ni se escribe la copia de nadie.
 */
export function llaveLocal(userId: string | null | undefined, clave: string): string | null {
  return userId ? `${PREFIJO_LOCAL}${userId}.${clave}` : null;
}

/** `localStorage` o `null` (modo privado, datos de sitio bloqueados: hasta leer la propiedad puede lanzar). */
export function almacenDelNavegador(): Almacen | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Lo guardado puede ser de una versión vieja o estar roto: siempre pasa por `sanear`. */
export function leerLocal<T>(userId: string | null | undefined, clave: string, sanear: (crudo: unknown) => T, porDefecto: T, almacen: Almacen | null): T {
  try {
    const llave = llaveLocal(userId, clave);
    const texto = llave ? almacen?.getItem(llave) : null;
    if (texto == null) return porDefecto;
    return sanear(JSON.parse(texto));
  } catch {
    return porDefecto;
  }
}

/** Nunca lanza: sin almacén (o sin usuario) la preferencia vive solo esta sesión. */
export function guardarLocal(userId: string | null | undefined, clave: string, valor: unknown, almacen: Almacen | null): void {
  try {
    const llave = llaveLocal(userId, clave);
    if (llave) almacen?.setItem(llave, JSON.stringify(valor));
  } catch {
    /* sin espacio o sin permiso: no se le avisa a nadie */
  }
}

/** Saneador para «una de estas cadenas»: lo que no esté en la lista vuelve al valor por defecto. */
export function unaDe<T extends string>(opciones: readonly T[], porDefecto: T): (crudo: unknown) => T {
  return (crudo) => (typeof crudo === 'string' && (opciones as readonly string[]).includes(crudo) ? (crudo as T) : porDefecto);
}
